import styled, { css } from 'styled-components';
import LinkComponent from '@components/link';
import { generateScales } from '@helpers/generate-media-queries';
import { defaultTheme } from '@styles/theme.styled';
import Image from '@components/image';

export const Divider = styled.div`
  ${({ theme }) => css`
    border-top: 0.1625rem solid ${theme.colors.primary};
    border-bottom: 0.1625rem solid ${theme.colors.primary};
    border-radius: 0.1625rem;
    height: 0.325rem;
    width: 4rem;
    transform-origin: left;
    align-self: center;
    margin-bottom: 0.75rem;
    transform-origin: 50% 0;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      border-top: 0.125rem solid ${theme.colors.primary};
      border-bottom: 0.125rem solid ${theme.colors.primary};
      height: 0.25rem;
      align-self: flex-start;
      width: 4rem;
      margin-bottom: 0.75rem;
    }
  `}
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;
  margin: 0 auto;
  width: 100%;
`;

export const FeatureListWrapper = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    width: 100%;
    row-gap: 1rem;
    column-gap: 4rem;
    margin-top: 1rem;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      column-gap: 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      grid-template-columns: repeat(1, 1fr);
      row-gap: 2.5rem;
      column-gap: 1rem;
      margin-top: 1rem;
    }
  `}
`;

export const FeatureItemWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: 0.625rem;
    align-items: flex-start;
    justify-content: flex-start;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 0.5rem;
    }
  `}
`;

export const IconWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    max-width: 1.25rem;
    max-height: 1.25rem;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      max-width: 1.125rem;
      max-height: 1.125rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      max-width: 1rem;
      height: 1rem;
    }
  `}
`;

export const FeatureColumnList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FeatureListColumn = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 1rem;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      gap: 1.5rem;
    }
  `}
`;

export const FeatureListIcon = styled(Image)`
  width: 100%;
  height: 100%;
`;

export const FeatureListDescription = styled.div`
  ${({ theme }) => css`
    ${generateScales('font-size', theme.fontSize.small.mobile, theme.fontSize.small.desktop, theme)}
  `}
`;

export const TextContent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 1.25rem;
    width: 65%;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      width: 100%;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      row-gap: 1rem;
      width: 100%;
    }
  `}
`;

export const ImageContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 35%;
    height: 100%;
    align-items: center;
    justify-content: flex-start;
    border-radius: ${theme.borderRadius.small};
    overflow: hidden;

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      height: unset;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      justify-content: center;
      width: 100%;
    }
  `}
`;

export const StyledImage = styled(Image)`
  ${({ theme }) => css`
    mix-blend-mode: multiply;
    width: 100%;
    height: 100%;

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      height: 100%;
    }
  `}
`;

export const StyledLinkComponent = styled(LinkComponent)`
  ${({ theme }) => css`
    width: fit-content;
    margin: 0;
    background-color: ${theme.colors.primary};
    color: ${theme.colors.black};
    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.body.desktop, theme)}
    transition: all 0.125s ease-in-out;

    &:hover {
      transform: scale(1.0375);
      background-color: ${theme.colors.black};
      color: ${theme.colors.white};
    }

    &:active {
      transform: scale(0.9625);
    }
  `}
`;

export const StyledItemLinkComponent = styled(LinkComponent)`
  ${({ theme }) => css`
    ${generateScales('font-size', theme.fontSize.small.mobile, theme.fontSize.small.desktop, theme)}
    margin: 0;
  `}
`;

interface FeatureWrapperProps {
  bgColor?: keyof typeof defaultTheme.backgroundColors;
  $imageAlignment?: 'default' | 'left' | 'right';
}

export const FeatureWrapper = styled.div<FeatureWrapperProps>`
  ${({ theme, bgColor, $imageAlignment }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5rem;
    border-radius: ${theme.borderRadius.large};
    padding: ${bgColor === 'white' ? '0 3rem' : '4rem 3rem'};
    background-color: ${bgColor === 'white'
      ? theme.backgroundColors.white
      : `${theme.backgroundColors.light}66`};
    ${$imageAlignment === 'default'
      ? css`
          flex-direction: ${bgColor === 'white' ? 'row' : 'row-reverse'};
        `
      : css`
          flex-direction: ${$imageAlignment === 'left' ? 'row' : 'row-reverse'};
        `}

    @media only screen and (max-width: ${theme.breakpoints.xxl}) {
      gap: 4.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      padding: ${bgColor === 'white' ? '0 3.5rem' : '3.5rem'};
      gap: 4rem;
      align-items: stretch;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      gap: 3rem;
      padding: ${bgColor === 'white' ? '0 3rem' : '3rem'};
      flex-direction: column;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      gap: 2rem;
      padding: ${bgColor === 'white' ? '0 2rem' : '2rem'};
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      border-radius: calc(${theme.borderRadius.large} / 2);
      padding: ${bgColor === 'white' ? '0 1rem' : '1.5rem 1rem 1.5rem 1rem'};
      gap: 1rem;
    }
  `}
`;
